import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Phone, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const RequestCallButton = ({ tool, defaultAddress = "", resultSummary = "", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState(defaultAddress);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const openDialog = () => {
    setAddress(defaultAddress || "");
    setMessage("");
    setOpen(true);
  };

  const handleSubmit = async () => {
    setSending(true);
    try {
      await api.post("/research/request-call", { tool, address, message, result_summary: resultSummary });
      toast.success("Request sent — an agent will be in touch soon.");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={openDialog}
        className={`border-[#547d91] text-[#547d91] hover:bg-[#547d91]/10 ${className}`}
        data-testid={`request-call-btn-${tool.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Phone className="w-4 h-4 mr-2" /> Request a call from agent
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="request-call-dialog">
          <DialogHeader>
            <DialogTitle>Request a call from an agent</DialogTitle>
            <DialogDescription>
              About <span className="font-medium text-slate-700">{tool}</span>. We'll email your request to
              an Investeps agent who'll get in touch.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="rc-address">Property / address (optional)</Label>
              <Input
                id="rc-address"
                data-testid="request-call-address"
                placeholder="e.g. 68 Fairfield Road, Fairfield QLD"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rc-message">What would you like to discuss?</Label>
              <Textarea
                id="rc-message"
                data-testid="request-call-message"
                rows={4}
                placeholder="Tell us what you'd like help with…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={sending}
              className="bg-[#547d91] hover:bg-[#456a7a]"
              data-testid="request-call-submit"
            >
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
